import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { NgRedux } from '@angular-redux/store';
import { Subscription } from 'rxjs';
import { IAppState, User } from '@core/redux/stores';
import {
  ProxyService,
  ModalService,
  JitMigrationService,
  AuthenticationService,
  AuthTrackingService
} from '@core/services';
import { ErrorCodesService } from '@core/services/error-codes.service';
import { NUDGE_TRIGGER } from '@core/services/migration-nudge-state.service';
import { ApiRoutes } from '@shared/models/configs/api.endpoints';
import { UpdateTrigger } from '@shared/components/validation-message';
import {
  applyAuth0OtpErrorUiState,
  resetAuth0OtpErrorUiState,
  resolveAuth0OtpHttpError,
} from '@features/authenticate/auth0-otp-error.util';

const RESEND_COOLDOWN_SECONDS = 60;

@Component({
  selector: 'app-jit-migration-modal',
  templateUrl: './jit-migration-modal.component.html',
  styleUrls: ['./jit-migration-modal.component.less'],
  providers: []
})
export class JitMigrationModalComponent implements OnInit, OnDestroy {
  step: 1 | 2 = 1;
  identifierForm: UntypedFormGroup;
  otpForm: UntypedFormGroup;
  loading = false;
  errorMessage = '';
  otpRateLimitBanner = '';
  otpContinueDisabled = false;
  otpResendDisabled = false;
  otpShowContactUs = false;
  identifierValue = '';
  resendCooldownSeconds = 0;
  updateTrigger = UpdateTrigger;
  readonly isMandatory: boolean;

  private user: User | null = null;
  private sub: Subscription | null = null;
  private resendTimer: ReturnType<typeof setInterval> | null = null;
  private outcomeHandled = false;

  constructor(
    private fb: UntypedFormBuilder,
    private ngRedux: NgRedux<IAppState>,
    private proxy: ProxyService,
    private modalService: ModalService,
    private jitMigrationService: JitMigrationService,
    private authService: AuthenticationService,
    private authTrackingService: AuthTrackingService
  ) {
    this.identifierForm = this.fb.group({
      identifier: new UntypedFormControl('', [Validators.required, Validators.email])
    });
    this.otpForm = this.fb.group({
      otpCode: new UntypedFormControl('', [Validators.required, Validators.minLength(6)])
    });
    this.isMandatory = this.jitMigrationService.getLastDecision()?.isMandatory ?? false;
  }

  /** Consumed by ModalComponent to hide the close control, which also disables Escape. */
  get isBlockingModal(): boolean {
    return this.isMandatory;
  }

  ngOnInit(): void {
    this.sub = this.ngRedux.select<User>((s) => s.user).subscribe((u) => {
      this.user = u;
      const email = u?.email ?? '';
      if (email && !this.identifierForm.get('identifier')?.value) {
        this.identifierForm.get('identifier')?.setValue(email);
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.clearResendTimer();
    //the close control and Escape bypass this component entirely, so leaving that way would otherwise
    //cost nothing and the nudge would return on every launch until the user dismissed or migrated
    this.recordAbandoned();
  }

  /**
   * A refresh or tab close destroys the page without running ngOnDestroy. Since the mandatory modal has
   * no close control, that is the only way a hard-blocked user leaves, so without this the web
   * abandonment count would always read zero and being stuck would look like nobody being stuck.
   */
  @HostListener('window:pagehide', ['$event'])
  onPageHide(event: PageTransitionEvent): void {
    //mandatory only: a dismissible showing still has its X and Cancel, which ngOnDestroy already covers,
    //and counting a refresh as a dismissal would spend a rung of the ladder the user never chose to spend
    if (!this.isMandatory) {
      return;
    }

    //persisted means the page is being frozen rather than destroyed, which is what mobile browsers do
    //when the user leaves to fetch the code from their inbox. They come back to the same modal and
    //finish, so treating that as abandonment would count most successful migrations as failures. The
    //trade is that a frozen page later discarded is a real abandonment we never see
    if (event?.persisted) {
      return;
    }
    this.recordAbandoned();
    //the SDK's own pagehide listener registered at bootstrap, so it runs first and flushes a queue that
    //does not yet hold this event. It has already flipped to sendBeacon by now, so this second flush is
    //what actually carries the event out
    this.jitMigrationService.flushEvents();
  }

  onDismiss(): void {
    this.recordAbandoned();
    this.modalService.hideModal();
  }

  onContinue(): void {
    resetAuth0OtpErrorUiState(this);
    const identifier = this.identifierForm.get('identifier')?.value?.trim();
    if (!identifier) {
      this.errorMessage = 'Please enter your email.';
      return;
    }
    if (this.identifierForm.get('identifier')?.hasError('email')) {
      this.errorMessage = 'Please enter a valid email address.';
      return;
    }

    this.identifierValue = identifier;
    this.initiateOtp();
  }

  private initiateOtp(): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    resetAuth0OtpErrorUiState(this);
    const linkToUserId = this.user?.id ?? '';
    if (!linkToUserId) {
      this.errorMessage = 'Unable to identify your account. Please sign in again.';
      this.loading = false;
      return;
    }
    const token = this.authTrackingService.getAuthToken();
    if (!token) {
      this.errorMessage = 'Session expired. Please sign in again.';
      this.loading = false;
      return;
    }
    const payload: Record<string, string> = {
      identifier: this.identifierValue,
      channel: 'email',
      linkToUserId,
      linkToEmail: this.identifierValue
    };
    this.proxy.httpPost(ApiRoutes.AUTH0_OTP_INITIATE, payload).subscribe(
      () => {
        this.loading = false;
        this.step = 2;
        this.otpForm.reset();
        this.startResendCooldown();
      },
      (err) => {
        this.loading = false;
        const code = err?.error?.code;
        if (code === 4062) {
          this.recordAbandoned();
          this.modalService.hideModal();
          return;
        }
        this.handleOtpHttpError(err, 'Failed to send code. Please try again.');
      }
    );
  }

  private handleOtpHttpError(
    err: { status?: number; error?: { code?: number; message?: string } },
    fallbackMessage: string
  ): void {
    const state = resolveAuth0OtpHttpError(err, fallbackMessage);
    applyAuth0OtpErrorUiState(state, this);
  }

  onResend(): void {
    if (this.loading || this.otpResendDisabled || this.resendCooldownSeconds > 0) {
      return;
    }
    this.initiateOtp();
  }

  onBack(): void {
    this.step = 1;
    resetAuth0OtpErrorUiState(this);
    this.otpForm.reset();
    this.clearResendTimer();
    this.resendCooldownSeconds = 0;
  }

  onVerify(): void {
    resetAuth0OtpErrorUiState(this);
    const raw = this.otpForm.get('otpCode')?.value ?? '';
    const code = (typeof raw === 'string' ? raw : '').replace(/\D/g, '');
    if (!code || code.length < 6) {
      this.errorMessage = 'Please enter the 6-digit code.';
      return;
    }
    this.loading = true;
    const linkToUserId = this.user?.id ?? '';
    const token = this.authTrackingService.getAuthToken();
    if (!linkToUserId || !token) {
      this.errorMessage = 'Session expired. Please sign in again.';
      this.loading = false;
      return;
    }
    const payload: Record<string, string> = {
      identifier: this.identifierValue,
      otpCode: code.slice(0, 6),
      channel: 'email',
      linkToUserId,
      linkToEmail: this.identifierValue
    };
    this.proxy.httpPost(ApiRoutes.AUTH0_OTP_VERIFY, payload).subscribe(
      (response: any) => {
        const body = response?.body ?? response;
        this.authService.setAuth0TokensFromOtpVerify(body, () => {
          this.outcomeHandled = true;
          this.jitMigrationService.recordMigrationCompleted();
          this.modalService.hideModal();
        });
        this.loading = false;
      },
      (err) => {
        this.loading = false;
        const errorCode = err?.error?.code;
        if (errorCode === 4058) {
          this.errorMessage = ErrorCodesService.getErrorMessage(4058, null);
          return;
        }
        this.handleOtpHttpError(err, 'Invalid code. Please try again.');
      }
    );
  }

  childClosed(): void {
    this.modalService.hideModal();
  }

  /**
   * Leaving the OTP flow unfinished counts as a dismissal, matching MAUI. A mandatory showing is the
   * exception and only logs, which JitMigrationService handles.
   */
  private recordAbandoned(): void {
    if (this.outcomeHandled) {
      return;
    }
    this.outcomeHandled = true;
    this.jitMigrationService.recordPromptDismissed(NUDGE_TRIGGER.abandonedOtp);
    //a hard-blocked user who gets out of here anyway lands back on the nudge rather than on an
    //unlocked page. No-op unless the showing was mandatory
    this.jitMigrationService.reopenMandatoryPrompt();
  }

  private startResendCooldown(): void {
    this.clearResendTimer();
    this.resendCooldownSeconds = RESEND_COOLDOWN_SECONDS;
    this.resendTimer = setInterval(() => {
      this.resendCooldownSeconds -= 1;
      if (this.resendCooldownSeconds <= 0) {
        this.clearResendTimer();
        this.resendCooldownSeconds = 0;
      }
    }, 1000);
  }

  private clearResendTimer(): void {
    if (this.resendTimer != null) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }
}
